const Offer = require('@/lib/Offers/Offer');
const { populate } = require('@/functions/objects');
const { toBool, toFloat, toInt, toNullableInt, toNullableString, toString } = require('../../../functions/transform');

/**
 * Represents an individual offer applied to a line in a cart (or order)
 * A line may have multiple discounts applying to a subset of the total qty of the line, and some discounts
 * might be inactive.
 *
 * @param {object} data
 *
 * @constructor
 */
const CartItemDiscount = function (data = {}) {
    /**
     * A discount can be added to a line but be inactive. This is the case when an offer was calculated for a line
     * but the operator has manually selected a discount for that line. The offer is still displayed for information
     * purposes only.
     *
     * @type {boolean}
     */
    this.active = true;

    /**
     * The total amount of discount this discount is giving (total for the line, not per qty on the line).
     *
     * @type {number}
     */
    this.amount = 0;

    /**
     * Description of the discount/offer.
     *
     * @type {string}
     */
    this.description = '';

    /**
     * Indicates if this discount was manually chosen by the operator by clicking the pencil/edit button on the line.
     *
     * @type {boolean}
     */
    this.manual = false;

    /**
     * How many of the items on this line does the discount apply to?
     * This is used because we do not split up cart lines like the frontend does. Instead we keep it as
     * one line and use this to keep track of which offers/discounts apply to what portion of each line.
     * null means it should apply to all of the remaining qty on the line that does not already have a discount.
     *
     * @type {number|null}
     */
    this.qty = 0;

    /**
     * ID of the Offer used to achieve the discount.
     *
     * @type {?number}
     */
    this.offerID = null;

    /**
     * This is only populated if the Offer is a Coupon Code Offer after the /v2/cartcalculations endpoint updates the
     * Offers and applies the Offers to the best lines.
     *
     * The /v2/cartcalculations endpoint expects an order level Offer Code, but the /v2/reservations endpoint expects
     * offersCodesIDs applied to individual lines.
     *
     * Please remember that at the moment due to app/API limitations there will only ever be one active Coupon Code per
     * order although that code may be applied to multiple lines.
     *
     * @type {null}
     */
    this.offersCodesID = null;

    /**
     * @type {Offer}
     */
    this.offer = null;

    /**
     * The percentage each item is discounted by (calculated if it is a 'value' discount)
     *
     * @type {number}
     */
    this.percentage = 0;

    /**
     * The type of discount applied (value or percentage or variable)
     *
     * @type {string}
     */
    this.type = '';

    /**
     * If this discount was created by an Experience Gift Voucher there should be a voucherCode
     *
     * @type {?string}
     */
    this.voucherCode = null;

    /**
     * The /cartcalculations API response includes a reason e.g. gift-voucher
     *
     * @type {?string}
     */
    this.reason = null;

    populate(this, data);
};

CartItemDiscount.prototype = {
    /**
     * @return {string}
     */
    getDescription() {
        if (this.description) {
            return this.description;
        }

        if (this.offer && this.offer.name) {
            return this.offer.name;
        }

        return '';
    },

    /**
     * @return {boolean}
     */
    isActive() {
        return this.active;
    },

    /**
     * @param {boolean} active
     */
    setActive(active) {
        this.active = !!active;
    },

    /**
     * @return {boolean}
     */
    isVariable() {
        // Check for the existence of an Experience Voucher which is a discount without an offer.
        return !!(this.offer && this.offer.isVariable());
    },

    /**
     * @param {Offer} offer
     */
    setOffer(offer) {
        this.offer = offer;
        this.offerID = offer.ID;
    },

    getHydrationMap() {
        return {
            active: toBool,
            amount: toFloat,
            description: toString,
            manual: toBool,
            offer: {
                model: Offer,
            },
            offerID: toNullableInt,
            offersCodesID: toNullableInt,
            percentage: toFloat,
            qty: toInt,
            reason: toNullableString,
            type: toString,
            voucherCode: toNullableString,
        };
    },
};

module.exports = CartItemDiscount;
